import { useEffect, useRef, useState } from 'react';
import type { Map as MapLibreMap, MapMouseEvent, StyleSpecification } from 'maplibre-gl';

/**
 * Карта находок на MapLibre GL (ТЗ 9.2 предполагал Leaflet).
 *
 * Почему не Leaflet. Он растровый: зум скачками, подписи в чужом наборе
 * цветов, и на телефоне тысяча точек заметно тормозит. MapLibre рисует
 * векторные тайлы на видеокарте — плавный зум, стиль в тон приложению,
 * кластеризация встроенная, без плагина.
 *
 * Три подложки, и каждая нужна за своё:
 *   — СХЕМА: OpenFreeMap, векторные тайлы OSM без ключа и без лимитов;
 *     светлая и тёмная версии переключаются вслед за темой приложения.
 *   — СПУТНИК: для месторождения это главный слой. Карьер, отвал и
 *     обнажение видно на снимке даже там, где в OSM пусто.
 *   — РЕЛЬЕФ: топооснова Esri — горизонтали, урочища, дороги. Здесь
 *     напрашивался OpenTopoMap, он для выездов нагляднее, но из России
 *     его тайлы просто не отдаются: проверка показала таймаут в 20 с.
 *
 * Библиотека грузится по требованию: 285 КБ ради экрана, на который
 * заходят раз в неделю, незачем возить в каждом запуске приложения. По
 * той же причине она исключена из офлайн-кэша: без тайлов из сети карта
 * всё равно покажет пустой экран.
 *
 * Версия намеренно пятая, не шестая: на 6.9 карта молча не запускает
 * отрисовку — стиль грузится, слои создаются, но ни один тайл не
 * запрашивается и события load не приходит. На 5.24 всё работает.
 */

export type MapPoint = {
  id: string;
  latitude: number;
  longitude: number;
  title: string;
  subtitle: string;
  precision_m?: number | null;
  source?: string | null;
};

export type BaseLayer = 'scheme' | 'satellite' | 'relief';

export const BASE_LAYERS: { key: BaseLayer; title: string }[] = [
  { key: 'scheme', title: 'Схема' },
  { key: 'satellite', title: 'Спутник' },
  { key: 'relief', title: 'Рельеф' },
];

const OPENFREEMAP = {
  light: 'https://tiles.openfreemap.org/styles/positron',
  dark: 'https://tiles.openfreemap.org/styles/dark',
};

/** Растровая подложка одним слоем — стиль целиком, без векторных данных. */
function rasterStyle(tiles: string, attribution: string, maxzoom = 18): StyleSpecification {
  return {
    version: 8,
    sources: {
      base: { type: 'raster', tiles: [tiles], tileSize: 256, maxzoom, attribution },
    },
    layers: [{ id: 'base', type: 'raster', source: 'base' }],
  };
}

function styleFor(layer: BaseLayer, dark: boolean): string | StyleSpecification {
  if (layer === 'satellite') {
    return rasterStyle(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      'Esri, Maxar, Earthstar Geographics',
      19,
    );
  }
  if (layer === 'relief') {
    return rasterStyle(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
      'Esri, HERE, Garmin, USGS, © OpenStreetMap',
      19,
    );
  }
  return dark ? OPENFREEMAP.dark : OPENFREEMAP.light;
}

function isDarkTheme(): boolean {
  const attribute = document.documentElement.dataset.theme;
  if (attribute === 'dark') return true;
  if (attribute === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Круг домашней зоны: в метрах, поэтому рисуется полигоном, а не точкой. */
function circle(lat: number, lng: number, radiusM: number, points = 64) {
  const coordinates: [number, number][] = [];
  const latRadius = radiusM / 111_320;
  const lngRadius = latRadius / Math.cos((lat * Math.PI) / 180);
  for (let i = 0; i <= points; i += 1) {
    const angle = (i / points) * 2 * Math.PI;
    coordinates.push([lng + lngRadius * Math.cos(angle), lat + latRadius * Math.sin(angle)]);
  }
  return {
    type: 'Feature' as const,
    properties: {},
    geometry: { type: 'Polygon' as const, coordinates: [coordinates] },
  };
}

/** Геометрия из тайла приходит общим типом; нам нужна только точка. */
function pointOf(geometry: unknown): [number, number] {
  const coordinates = (geometry as { coordinates?: unknown }).coordinates;
  return Array.isArray(coordinates) ? (coordinates as [number, number]) : [0, 0];
}

function toGeoJson(points: MapPoint[]) {
  return {
    type: 'FeatureCollection' as const,
    features: points.map((point) => ({
      type: 'Feature' as const,
      properties: {
        id: point.id,
        title: point.title,
        subtitle: point.subtitle,
        precision: point.precision_m ?? null,
      },
      geometry: { type: 'Point' as const, coordinates: [point.longitude, point.latitude] },
    })),
  };
}

export function MapView({
  points,
  onPick,
  picked,
  onSelect,
  className,
  layer = 'scheme',
}: {
  points: MapPoint[];
  /** Режим постановки точки: нажатие по карте отдаёт координаты. */
  onPick?: (lat: number, lng: number) => void;
  picked?: { latitude: number; longitude: number; radius_m?: number } | null;
  onSelect?: (id: string) => void;
  className?: string;
  layer?: BaseLayer;
}) {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibreMap | null>(null);
  const lib = useRef<typeof import('maplibre-gl') | null>(null);
  const observer = useRef<ResizeObserver | null>(null);
  const [ready, setReady] = useState(false);

  // Обработчики меняются на каждый рендер родителя; карта живёт дольше,
  // поэтому она читает их через ref, а не пересоздаётся.
  const pickHandler = useRef(onPick);
  const selectHandler = useRef(onSelect);
  pickHandler.current = onPick;
  selectHandler.current = onSelect;

  const pointsRef = useRef(points);
  const pickedRef = useRef(picked);
  pointsRef.current = points;
  pickedRef.current = picked;

  /**
   * Подписи по-русски там, где они есть в данных.
   *
   * Стиль OpenFreeMap по умолчанию подписывает всё латиницей и дублирует
   * местное название второй строкой: «MOSCOW / МОСКВА». Для каталога,
   * который ведут по-русски, это шум — берём name:ru, а латиницу
   * оставляем там, где русского имени нет.
   */
  function russianLabels(instance: MapLibreMap): void {
    for (const layer of instance.getStyle().layers ?? []) {
      if (layer.type !== 'symbol') continue;
      const field = (layer.layout as { 'text-field'?: unknown } | undefined)?.['text-field'];
      if (!field) continue;
      try {
        instance.setLayoutProperty(layer.id, 'text-field', [
          'coalesce',
          ['get', 'name:ru'],
          ['get', 'name:latin'],
          ['get', 'name'],
        ]);
      } catch {
        // Слой без обычного текстового поля — оставляем как есть.
      }
    }
  }

  /**
   * Слои данных живут поверх подложки и пересоздаются при её смене:
   * setStyle сбрасывает всё, что было добавлено поверх стиля.
   */
  function addLayers(instance: MapLibreMap): void {
    if (!lib.current) return;

    if (!instance.getSource('specimens')) {
      instance.addSource('specimens', {
        type: 'geojson',
        data: toGeoJson(pointsRef.current),
        cluster: true,
        clusterRadius: 50,
        clusterMaxZoom: 12,
      });

      instance.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'specimens',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#2f62a8',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 3,
          // Кластер из трёх точек и кластер из сотни не должны выглядеть
          // одинаково: размер растёт вместе с числом.
          'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 50, 26],
        },
      });
      instance.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'specimens',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-size': 13,
          'text-font': ['Noto Sans Medium', 'Open Sans Semibold', 'Arial Unicode MS Bold'],
        },
        paint: { 'text-color': '#ffffff' },
      });
      instance.addLayer({
        id: 'point',
        type: 'circle',
        source: 'specimens',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#ffffff',
          'circle-radius': 7,
          'circle-stroke-color': '#2f62a8',
          'circle-stroke-width': 3,
        },
      });
    }

    if (!instance.getSource('picked')) {
      instance.addSource('picked', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      instance.addLayer({
        id: 'picked-zone',
        type: 'fill',
        source: 'picked',
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': '#a83a2c', 'fill-opacity': 0.12 },
      });
      instance.addLayer({
        id: 'picked-zone-edge',
        type: 'line',
        source: 'picked',
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'line-color': '#a83a2c', 'line-width': 1 },
      });
      instance.addLayer({
        id: 'picked-point',
        type: 'circle',
        source: 'picked',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-color': '#ffffff',
          'circle-radius': 8,
          'circle-stroke-color': '#a83a2c',
          'circle-stroke-width': 3,
        },
      });
    }

    syncPicked(instance);
  }

  function syncPicked(instance: MapLibreMap): void {
    const source = instance.getSource('picked');
    if (!source || !('setData' in source)) return;
    const value = pickedRef.current;

    const features = [];
    if (value) {
      if (value.radius_m) features.push(circle(value.latitude, value.longitude, value.radius_m));
      features.push({
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'Point' as const,
          coordinates: [value.longitude, value.latitude],
        },
      });
    }
    (source as { setData: (data: unknown) => void }).setData({
      type: 'FeatureCollection',
      features,
    });
  }

  // ── Создание карты ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const maplibre = await import('maplibre-gl');
      await import('maplibre-gl/dist/maplibre-gl.css');
      if (cancelled || !host.current || map.current) return;

      lib.current = maplibre;
      const instance = new maplibre.Map({
        container: host.current,
        style: styleFor(layer, isDarkTheme()),
        center: [60, 60],
        zoom: 2.5,
        attributionControl: { compact: true },
        // Поворот на телефоне чаще мешает, чем помогает: карту крутят
        // случайно двумя пальцами и потом не могут вернуть север наверх.
        pitchWithRotate: false,
        dragRotate: false,
      });
      instance.touchZoomRotate.disableRotation();
      instance.addControl(new maplibre.NavigationControl({ showCompass: false }), 'top-left');

      // style.load — единственное событие, после которого стиль точно
      // готов принимать чужие слои. И оно повторяется при смене подложки,
      // а load — только один раз за жизнь карты.
      instance.on('style.load', () => {
        russianLabels(instance);
        addLayers(instance);
      });

      instance.on('load', () => {
        fitToPoints(instance);
        if (!cancelled) setReady(true);
      });

      // Контейнер получает высоту позже, чем создаётся карта: он лежит
      // во flex-колонке, и на момент инициализации она ещё не разложена.
      // Без этого карта считает себя нулевого размера и не запрашивает
      // ни одного тайла — виден пустой серый прямоугольник.
      const resizer = new ResizeObserver(() => instance.resize());
      resizer.observe(host.current!);
      observer.current = resizer;
      instance.on('click', (event: MapMouseEvent) => {
        pickHandler.current?.(event.lngLat.lat, event.lngLat.lng);
      });

      instance.on('click', 'clusters', (event) => {
        const feature = event.features?.[0];
        if (!feature) return;
        const source = instance.getSource('specimens') as unknown as {
          getClusterExpansionZoom: (id: number) => Promise<number>;
        };
        void source
          .getClusterExpansionZoom(feature.properties!.cluster_id as number)
          .then((zoom) => {
            const [lng, lat] = pointOf(feature.geometry);
            instance.easeTo({ center: [lng, lat], zoom });
          });
      });

      instance.on('click', 'point', (event) => {
        const feature = event.features?.[0];
        if (!feature) return;
        const [lng, lat] = pointOf(feature.geometry);
        const properties = feature.properties as {
          id: string;
          title: string;
          subtitle: string;
          precision: number | null;
        };

        const node = document.createElement('div');
        node.className = 'map-popup';
        const title = document.createElement('b');
        title.textContent = properties.title;
        const subtitle = document.createElement('div');
        subtitle.textContent = properties.subtitle;
        node.append(title, subtitle);
        if (properties.precision) {
          const hint = document.createElement('div');
          hint.className = 'hint';
          hint.textContent = `±${properties.precision} м`;
          node.append(hint);
        }
        if (selectHandler.current) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'map-open';
          button.textContent = 'Открыть образец';
          button.addEventListener('click', () => selectHandler.current?.(properties.id));
          node.append(button);
        }

        new maplibre.Popup({ closeButton: false, offset: 12 })
          .setLngLat([lng, lat])
          .setDOMContent(node)
          .addTo(instance);
      });

      for (const id of ['clusters', 'point']) {
        instance.on('mouseenter', id, () => {
          instance.getCanvas().style.cursor = 'pointer';
        });
        instance.on('mouseleave', id, () => {
          instance.getCanvas().style.cursor = '';
        });
      }

      map.current = instance;
    })();

    return () => {
      cancelled = true;
      observer.current?.disconnect();
      observer.current = null;
      map.current?.remove();
      map.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fitToPoints(instance: MapLibreMap): void {
    const maplibre = lib.current;
    const list = pointsRef.current;
    if (!maplibre || list.length === 0) return;

    const bounds = new maplibre.LngLatBounds();
    for (const point of list) bounds.extend([point.longitude, point.latitude]);
    instance.fitBounds(bounds, { padding: 48, maxZoom: 11, duration: 0 });
  }

  // ── Обновления ────────────────────────────────────────────────────
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    const source = instance.getSource('specimens');
    if (source && 'setData' in source) {
      (source as { setData: (data: unknown) => void }).setData(toGeoJson(points));
      fitToPoints(instance);
    }
  }, [points, ready]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    syncPicked(instance);
    if (picked) {
      instance.easeTo({
        center: [picked.longitude, picked.latitude],
        zoom: Math.max(instance.getZoom(), 11),
      });
    }
  }, [picked?.latitude, picked?.longitude, picked?.radius_m, ready]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    instance.setStyle(styleFor(layer, isDarkTheme()));
  }, [layer, ready]);

  // Тему меняют системой, и карта должна переодеться вместе с приложением,
  // а не остаться светлым прямоугольником до перезахода.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const instance = map.current;
      if (instance && ready) instance.setStyle(styleFor(layer, isDarkTheme()));
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [layer, ready]);

  return <div ref={host} className={className ?? 'map'} />;
}
